import React, { ChangeEvent, useEffect, useState } from 'react'
import CouponPurchaseView from 'src/pages/CouponPurchasePage/view'
import { QueryClient } from '@tanstack/react-query'
import { Params } from 'react-router-dom'
import {
  couponDetailQueryOption,
  serviceTermsQueryOption,
  useCustomQuery,
} from 'src/hooks/react-query/useCustomQuery.hook'
import {
  CouponDetailResponseType,
  PaymentGatewayRequestSpace,
  PaymentWindowRequestCreditCardSpace,
  PGType,
  ResponseDataType,
} from 'src/types/api'
import usePageVisibility from 'src/hooks/usePageVisibility.hook'
import Payment from 'src/components/common/Payment'
import useParamsType, { MatchParams } from 'src/hooks/useParamsType.hook'
import useBillgate from 'src/hooks/useBillgate.hook'
import { useCookies } from 'react-cookie'
import RenderIf from 'src/components/common/RenderIf'
import { Loading } from 'src/components/common/Loading'
import { useChannel } from 'src/provider/ChannelContextProvider'
import naver_pay_img from 'src/assets/images/purchase/naver_pay.png'
import samsung_pay_img from 'src/assets/images/purchase/samsung_pay.png'
import payco_img from 'src/assets/images/purchase/payco.png'
import apple_pay_img from 'src/assets/images/purchase/apple_pay.png'
import kakao_pay_img from 'src/assets/images/purchase/kakao_pay.png'
import { useModal } from 'src/provider/ModalContextProvider'
import TermsModal from 'src/components/common/Modal/children/TermsModal'
import usePayment from 'src/hooks/usePayment.hook'
import { detectDevice, handleJsonParse, randomPaymentOrderId } from 'src/utils'
import {
  paymentGatewayQueryOption,
  paymentWindowCreditCardRequestQueryOption,
  useCustomMutation,
} from 'src/hooks/react-query/useCustomMutation.hook'
import {
  ERRCODE_SUCCESS,
  ERRCODES_COMMON,
  ERRCODES_PURCHASE_VALID_CHECK,
} from 'src/service/constant/errcodes'
import { useScript } from 'src/hooks/useScript.hook'
import useDanal from 'src/hooks/useDanal.hook'
import usePageNav from 'src/hooks/usePageNav.hook'
import { useToast } from 'src/provider/ToastContextProvider'
import { addFlowBreadcrumb } from 'src/utils/sentry/breadcrumbs'
import useSamsungPay from 'src/hooks/useSamsungPay.hook'

export type PaymentMethodGroup = 'general' | 'easy'

export type paymentRadioButtonItemsType = {
  id: string
  value: string
  img: { src: string; alt: string; size: string }
  group: PaymentMethodGroup
}

export interface CouponPurchaseProps {
  handleServiceTermsModal: () => void
  couponDetailData?: CouponDetailResponseType
  handlePaymentCallSubmit: () => void
  isSubmiting: boolean
  radioButtonItems: paymentRadioButtonItemsType[]
  handleSetPaymentMethod: (e: ChangeEvent<HTMLInputElement>) => void
  activePaymentMethod: string
}

export const couponPurchasePageLoader =
  (queryClient: QueryClient) =>
  async ({ params: { couponType, couponIdx } }: { params: Params<keyof MatchParams> }) => {
    return Promise.allSettled([
      await queryClient.ensureQueryData(
        couponDetailQueryOption(couponType!, { CouponIdx: Number(couponIdx!) }),
      ),
      await queryClient.ensureQueryData(serviceTermsQueryOption({ Type: 2 })),
    ])
  }

export const paymentRadioButtonItems: paymentRadioButtonItemsType[] = [
  {
    id: 'CARD',
    value: process.env.REACT_APP_PAYMENT_CARD_SERVICE_ID as string,
    img: { src: '', alt: '신용·체크카드', size: '' },
    group: 'general',
  },
  {
    id: 'MOBILE',
    value: '',
    img: { src: '', alt: '휴대폰소액결제', size: '' },
    group: 'general',
  },
  {
    id: 'KAKAOPAY',
    value: process.env.REACT_APP_PAYMENT_KAKAO_SERVICE_ID as string,
    img: { src: kakao_pay_img, alt: 'Kakao Pay', size: '54px' },
    group: 'easy',
  },
  {
    id: 'SAMSUNGPAY',
    value: process.env.REACT_APP_PAYMENT_SAMSUNG_SERVICE_ID as string,
    img: { src: samsung_pay_img, alt: 'Samsung Pay', size: '54px' },
    group: 'easy',
  },
  //TODO: 페이코 제외 검수 테스트 진행(260814)
  // {
  //   id: 'PAYCO',
  //   value: '',
  //   img: { src: payco_img, alt: '페이코', size: '59px' },
  //   group: 'easy',
  // },
  // {
  //   id: 'TMONEY',
  //   value: process.env.REACT_APP_PAYMENT_TMONEY_SERVICE_ID as string,
  //   img: { src: '', alt: '티머니', size: '' },
  //   group: 'easy',
  // },
  // {
  //   id: 'NAVERPAY',
  //   value: process.env.REACT_APP_PAYMENT_NAVER_SERVICE_ID as string,
  //   img: { src: naver_pay_img, alt: 'Naver Pay', size: '56px' },
  //   group: 'easy',
  // },
  // ...(handleDeviceDetect().channel === 'ios'
  //   ? [
  //       {
  //         id: 'APPLEPAY',
  //         value: process.env.REACT_APP_PAYMENT_APPLE_SERVICE_ID as string,
  //         img: { src: apple_pay_img, alt: 'Apple Pay', size: '38px' },
  //         group: 'easy' as const,
  //       },
  //     ]
  //   : []),
]

const firstRadioButtonItems = paymentRadioButtonItems?.at(0)?.id as string

const CouponPurchasePage = () => {
  const { couponIdx, couponType, couponNumber } = useParamsType()
  const paymentRequestUrl = process.env.REACT_APP_PAYMENT_REQUEST_URL as string
  const status = useScript(paymentRequestUrl, { removeOnUnmount: true })
  const [cookies, setCookie] = useCookies(['orderId', 'pg'])
  const [deemToggle, setDeemToggle] = useState(false)
  const [activePaymentMethod, setActivePaymentMethod] = useState<string>(firstRadioButtonItems)
  // 결제창 form은 주문 요청에 보낸 값을 그대로 써야 한다. 렌더마다 다시 만들면 ORDER_DATE가 어긋난다
  const [paymentFormData, setPaymentFormData] =
    useState<PaymentWindowRequestCreditCardSpace.BillgateRequest>()
  const { openModal } = useModal()
  const { openToast } = useToast()
  const { couponDetailPageNav } = usePageNav()
  const { aos, web, channel } = useChannel()
  const { handlePaymentResponse, paymentRequestData, pgWindow, createOrderDate } = usePayment()
  const { handleBillgatePaymentRequest } = useBillgate()
  const { handleDanalPaymentReqeust } = useDanal()
  const { handleSamsungIsPayRequest } = useSamsungPay()
  const pc = detectDevice() === 'pc'

  // 구매 및 개인정보 수집 약관 데이터
  const { data: termsQueryData } = useCustomQuery(serviceTermsQueryOption({ Type: 2 }))

  // 쿠폰 상세 데이터
  const { data: couponDetailQueryData, isLoading: isCouponDetailLoading } =
    useCustomQuery<CouponDetailResponseType>(
      couponDetailQueryOption(couponType, { CouponIdx: Number(couponIdx!) }),
    )

  //PG사 선택
  const { mutateAsync: paymentGatewayRequest, isPending: isGatewayPending } = useCustomMutation<
    PaymentGatewayRequestSpace.Request,
    PaymentGatewayRequestSpace.Response
  >(paymentGatewayQueryOption())

  //결제창 호출 정보 서버에 저장
  const { mutateAsync: paymentCreditCardSubmit, isPending: isPaymentPending } = useCustomMutation<
    | PaymentWindowRequestCreditCardSpace.BillgateRequest
    | PaymentWindowRequestCreditCardSpace.DanalRequest,
    PaymentWindowRequestCreditCardSpace.Response
  >(paymentWindowCreditCardRequestQueryOption())

  // 구매 약관 모달 호출
  const handleServiceTermsModal = () => {
    const t = termsQueryData?.Body?.Title
    const contents = termsQueryData?.Body?.Contents

    openModal({
      button: { submit: { t: '닫기' } },
      contents: { t, children: <TermsModal children={contents as string} /> },
    })
  }

  //결제창 호출전 서버에 데이터 저장 후 결제창 호출
  const handlePaymentCallSubmit = async () => {
    let pg = 'billgate' as PGType
    const needsPgDecision = ['CARD', 'KAKAOPAY', 'SAMSUNGPAY'].includes(activePaymentMethod)
    if (needsPgDecision) {
      const { Header, Body } = await paymentGatewayRequest({
        PayMethod: activePaymentMethod,
        MobileWeb: web && !pc,
      })
      if (Header.ErrCode !== ERRCODE_SUCCESS) {
        addFlowBreadcrumb('payment_fail', 'response', {
          handler: 'paymentGatewayRequest',
          errCode: Header.ErrCode,
          errMsg: Header.ErrMsg,
        })
        return openToast({ contents: '결제창 호출에 실패했습니다.' })
      }
      pg = Body?.PG as PGType
    }

    const pgForWindow = pgWindow(pg, activePaymentMethod)
    const CreateOrderId = randomPaymentOrderId()
    const CreateOrderDate = createOrderDate()
    setCookie('pg', pg, { path: '/' })
    setCookie('orderId', CreateOrderId, { path: '/' })

    const paymentData = paymentRequestData({
      pg: pg,
      payMethod: activePaymentMethod,
      itemNameSlice: false,
      couponData: couponDetailQueryData?.Body!,
      orderId: CreateOrderId,
      orderDate: CreateOrderDate,
    })
    setPaymentFormData(
      paymentRequestData({
        pg: pg,
        payMethod: activePaymentMethod,
        itemNameSlice: true,
        couponData: couponDetailQueryData?.Body!,
        orderId: CreateOrderId,
        orderDate: CreateOrderDate,
      }) as PaymentWindowRequestCreditCardSpace.BillgateRequest,
    )
    addFlowBreadcrumb('payment_attempt', 'request', {
      handler: 'paymentCreditCardSubmit',
      payMethod: activePaymentMethod,
      pg,
      type: 'default',
    })

    paymentCreditCardSubmit(paymentData).then(
      ({ Header, Body }: ResponseDataType<PaymentWindowRequestCreditCardSpace.Response>) => {
        if (Header.ErrCode === ERRCODE_SUCCESS) {
          // 삼성페이만 ISPAY(StartURL) 경로. 그 외는 주문 요청에 보낸 PG를 그대로 사용
          if (activePaymentMethod === 'SAMSUNGPAY' && Body.StartURL) {
            handleSamsungIsPayRequest({
              StartURL: Body.StartURL,
              TID: Body.TID,
              StartParams: Body.StartParams,
              OrderID: Body.OrderID,
            })
          } else if (pgForWindow === 'danal') {
            handleDanalPaymentReqeust(paymentData)
          } else {
            if (status !== 'ready') return
            handleBillgatePaymentRequest({
              orderId: Body.OrderID,
              payMethod: activePaymentMethod,
              hashKey: Body.HashKey,
              orderDate: CreateOrderDate,
            })
          }
          setDeemToggle(true)
        } else {
          const ErrMsg = handleJsonParse(Header.ErrMsg)
          const { Title = '', Contents = '' } = ErrMsg
          switch (Header.ErrCode) {
            case ERRCODES_PURCHASE_VALID_CHECK.ERROR_NOT_VALID_COUPON:
            case ERRCODES_PURCHASE_VALID_CHECK.ERROR_HOT_DEAL_SOLD_OUT:
            case ERRCODES_PURCHASE_VALID_CHECK.ERROR_ONLY_ONE_HOTDEAL_PURCHASE:
            case ERRCODES_PURCHASE_VALID_CHECK.ERROR_DAILY_PURCHASED:
              openModal({
                contents: { t: Title, d: Contents },
                button: { submit: { t: '확인' } },
              })
              break
            case ERRCODES_PURCHASE_VALID_CHECK.ERROR_DIFFERENT_PRICE:
              const handleSubmitButton = () =>
                couponDetailPageNav(
                  couponNumber,
                  Number(couponIdx),
                  Number(couponType) as 0 | 1 | 2,
                )

              openModal({
                contents: { t: Title, d: Contents },
                button: { submit: { t: '확인', f: handleSubmitButton } },
              })
              break
            case ERRCODES_PURCHASE_VALID_CHECK.ERROR_MAX_OF_MONTH_HOTDEAL_PURCHASE:
              openModal({
                contents: { t: Title, d: Contents },
                button: { submit: { t: '닫기' } },
              })
              break
            //통신 모듈에 있는 에러 핸들링 사용
            case ERRCODES_COMMON.PIN_CHECK_ERROR:
            case ERRCODES_COMMON.SESSION_ERROR:
            case ERRCODES_COMMON.ERROR_LOGOUT:
              break
            default:
              openModal({ contents: { d: Header.ErrMsg }, button: { submit: { t: '닫기' } } })
              break
          }
        }
      },
    )
  }

  const handleSetPaymentMethod = (e: ChangeEvent<HTMLInputElement>) => {
    setActivePaymentMethod(e.target.id)
  }

  const handlePaymentInProgressDeem = () => {
    handlePaymentResponse('default', cookies['orderId'] as string)
    setDeemToggle(false)
  }

  //app
  usePageVisibility((isForeground: boolean) => {
    if (isForeground) {
      if (!web && cookies['orderId'] !== undefined && deemToggle) {
        addFlowBreadcrumb('payment_confirm', 'request', {
          handler: 'paymentReturnFromPg',
          channel,
        })
        handlePaymentInProgressDeem()
      }
    }
  })

  //aos
  useEffect(() => {
    if (aos && cookies['orderId'] !== undefined) {
      addFlowBreadcrumb('payment_confirm', 'request', {
        handler: 'paymentReturnFromPg',
        channel,
      })
      handlePaymentResponse('default', cookies['orderId'] as string)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const props: CouponPurchaseProps = {
    couponDetailData: couponDetailQueryData?.Body,
    handleServiceTermsModal,
    handlePaymentCallSubmit,
    isSubmiting: isGatewayPending || isPaymentPending,
    radioButtonItems: paymentRadioButtonItems,
    handleSetPaymentMethod,
    activePaymentMethod,
  }

  if (deemToggle) {
    return <Loading.PaymentCouponInProgress onClickFn={handlePaymentInProgressDeem} />
  }

  return (
    <React.Fragment>
      <RenderIf when={isCouponDetailLoading}>
        <Loading.Default />
      </RenderIf>
      <CouponPurchaseView {...props} />
      {web && paymentFormData && <Payment paymentData={paymentFormData} />}
    </React.Fragment>
  )
}
export default CouponPurchasePage
